import { useState, useEffect } from 'react';
import { Form, Input, Select, Button, Space, message, Tag, Tabs, InputNumber, DatePicker } from 'antd';
import { UserAddOutlined, SearchOutlined, ReloadOutlined, PlusOutlined, MinusCircleOutlined } from '@ant-design/icons';
import ReactJson from '@microlink/react-json-view';
import { getChannelLogo } from '../utils/channelLogos';
import { testApi, metaApi } from '../services/api';
import { useLanguage } from '../contexts/LanguageContext';

const { TextArea } = Input;
const CHANNELS = ['PYVIO', 'TAZAPAY', 'MDAQ'];

const TAZAPAY_COMPANY_TYPES = ['sole_proprietorship', 'company', 'non_profit', 'government_entity'];

const TAZAPAY_INDUSTRY_OPTIONS = [
  { label: '📱 Digital Products', options: [
    'Digital Products - Apps, eBooks, Blogs & Games','Digital Products - Digital Merchandise','Digital Products - Music, Video, Channels & Podcast',
    'Digital Products - Online Gaming (based on skill)','Digital Products - Other Digital Goods','Digital Products - Large Digital Goods Merchant (>25 Mn Txns annually)',
  ].map(v => ({ label: v, value: v }))},
  { label: '🛒 Ecommerce', options: [
    'Ecommerce - Consumer Electronics','Ecommerce - Consumer Electronics (Mobiles)','Ecommerce - Beauty Products & Cosmetics',
    'Ecommerce - General Merchandise','Ecommerce - Home Appliances','Ecommerce - Dropshipping','Ecommerce - Diversified/Others',
    'Ecommerce - Men and boys clothing','Ecommerce - Women fashion','Ecommerce - Children clothing','Ecommerce - Sports clothing',
    'Ecommerce - General clothing stores (for men, women, children)','Ecommerce - General clothing stores (for men and women)',
    'Ecommerce - Footwear including athletic footwear','Ecommerce - Sports & Travel Goods','Ecommerce - Books Stores',
    'Ecommerce - Food & Non-Alcohol Drinks (Grocery and Supermarkets)','Ecommerce - Food & Non-Alcohol Drinks (Bakeries)',
    'Ecommerce - Food & Non-Alcohol Drinks (Confectionery / Candy)','Ecommerce - Food & Non-Alcohol Drinks (Dairy products)',
    'Ecommerce - Food & Non-Alcohol Drinks (Miscellaneous / Specialty stores)','Ecommerce - Pet Food',
    'Ecommerce - Meat & Poultry','Ecommerce - Supplements or Nutraceuticals',
    'Ecommerce - Home interior fixtures and decorating items','Ecommerce - Home, Decor & Furniture-Warehouse Stores',
    'Ecommerce - Finished Furniture and accessories','Ecommerce - Hobby, Toy, and Game Shops',
    'Ecommerce - Artwork / Art Dealers and Galleries','Ecommerce - Art & Craft Materials, Handicraft and tools',
    'Ecommerce - Aroma, Organic & Herbal','Ecommerce - Packaging, Office & Stationery',
    'Ecommerce - Electronic Hardware, Tools & Parts','Ecommerce - Hardware stores',
    'Ecommerce - Electrical and plumbing supplies, tools and power tools',
    'Ecommerce - Building & Construction Materials (General)','Ecommerce - Building & Construction Materials (Glass, Paint and Wallpaper)',
    'Ecommerce - Building & Construction Materials (Floor Covering)',
    'Ecommerce - Automobiles - Automobile sale/lease and repair, replacement of parts',
    'Ecommerce - Automobiles - Automobile parts/tools, accessories and equipment',
    'Ecommerce - Automobiles - Automobile tyre sale, installation and repair',
    'Ecommerce - Automobiles - Motorcycle Shops and Dealers','Ecommerce - Automobiles - Bicycles, bicycle parts, accessories and repair',
    'Ecommerce - Automobiles - Car and Truck Dealers (Used Only) Sales, Service, Repairs, Parts, and Leasing',
    'Ecommerce - Automobiles (B2B) - Motor Vehicle Supplies and New Parts',
    'Ecommerce - Camera and Photographic Supply Stores','Ecommerce - Clothing rental','Ecommerce - Custom made clothing (to order)',
    'Ecommerce - Discount Stores','Ecommerce - Drapery, Window Covering, and Upholstery Stores','Ecommerce - Duty Free Stores',
    'Ecommerce - Equipment, Tool, Furniture, and Appliance Rental and Leasing',
    'Ecommerce - Fur jackets, coats','Ecommerce - Gift, Card, Novelty and Souvenir Shops',
    'Ecommerce - Glassware/Crystal Stores','Ecommerce - Luggage and storage (incl backpacks, briefcases, purses and wallets)',
    'Ecommerce - Miscellaneous Clothing (e.g., Swimwear)','Ecommerce - Music Stores',
    'Ecommerce - AgriProducts - Garden Products and Fertilizers','Ecommerce - AgriProducts - Florists and Novelty Products',
    'Ecommerce - Religious Goods Stores','Ecommerce - Sewing, Needlework, Fabric and Piece Goods Stores',
    'Ecommerce - Stamp and Coin Stores','Ecommerce - Used Merchandise and Secondhand Stores',
    'Ecommerce - Wigs, Toupes, hair pieces and extensions','Ecommerce - Women accessories and specialty shops',
    'Ecommerce (B2B) - Building & Construction Materials (General)',
    'Ecommerce (B2B) - Uniform supply and Commercial / Industrial clothing',
    'Ecommerce (B2B) - Clocks, watches, silverware gems and jewelry','Ecommerce (B2B) - Commercial footwear',
    'Ecommerce (B2B / Others) - Medical, dental, ophthalmic and hospital equipment and supplies & PPE',
    'Ecommerce (B2C) - Clocks, watches, silverware gems and jewelry',
  ].map(v => ({ label: v, value: v }))},
  { label: '🎓 Education', options: [
    'Education - Elementary and Secondary Schools','Education - Elearning, Online Tutoring, Homework & Test Prep',
    'Education - Colleges, Universities, Professional Schools, and Junior Colleges',
    'Education - Online Education and Correspondence Schools',
    'Education (others) - Other Educational Services not elsewhere classified',
    'Education (others) - Business and Secretarial Schools','Education (others) - Vocational and Trade Schools',
  ].map(v => ({ label: v, value: v }))},
  { label: '💰 Financial Service', options: [
    'Financial Service - Digital Wallets, PSPs & Fintech','Financial Service - Banks and NBFI',
    'Financial Service - Brokerages & Exchanges','Financial Service - Remittance & MSB',
    'Financial Service - Life and Non-Life Insurance policies','Financial Service - Investment Banking & Financial Advisory',
    'Financial Service - Fund, Wealth Management & Investment Holding','Financial Service - CFD and Forex',
    'Financial Service - Carbon Credit','Financial Service - Crowdfunding platform (equity)',
    'Financial Service - Crowdfunding platforms (debt)','Financial Service - Prop Trading Simulation',
    'Financial Service - Other Financial Services (Automated Cash disbursements)',
    'Financial Service - Other Financial Services (Manual Cash disbursements)',
    'Financial Service - Other Financial Services (Non-classified)','Financial Services - Intra-Company Purchases',
  ].map(v => ({ label: v, value: v }))},
  { label: '🏭 General Industries', options: [
    'General Industries - Agri, Forestry & Fishing',
    'General Industries - Trade & Wholesale of General Goods (Commercial Equipment)',
    'General Industries - Trade & Wholesale of General Goods (Chemicals and Allied Products)',
    'General Industries - Trade & Wholesale of General Goods (Computers Hardware, Software)',
    'General Industries - Trade & Wholesale of General Goods (Durable Goods)',
    'General Industries - Trade & Wholesale of General Goods (Flower)',
    'General Industries - Trade & Wholesale of General Goods (Hardware, Equipment and Supplies)',
    'General Industries - Trade & Wholesale of General Goods (Industrial Supplies)',
    'General Industries - Trade & Wholesale of General Goods (Semi-finished metal products)',
    'General Industries - Trade & Wholesale of General Goods (Non-durable goods)',
    'General Industries - Trade & Wholesale of General Goods (Office and Commercial Furniture)',
    'General Industries - Trade & Wholesale of General Goods (Paints and related equipment)',
    'General Industries - Trade & Wholesale of General Goods (Photographic equipment, etc)',
    'General Industries - Trade & Wholesale of General Goods (Piece Goods, Notions, and Other Dry Goods)',
    'General Industries - Trade & Wholesale of General Goods (Commercial machines and equipment)',
    'General Industries - Trade & Wholesale of General Goods (Stationery, Office Supplies)',
    'General Industries - Trade & Wholesale of General Goods (Typewriter Stores)',
    'General Industries - Trade & Wholesale of General Goods (Electrical Parts and equipment)',
  ].map(v => ({ label: v, value: v }))},
  { label: '🔧 General Service', options: [
    'General Service - Other General Services','General Service - Computer Network/Information Services',
    'General Service - Health and Beauty Spas','General Service - Photography Studios',
    'General Service - Recreation Services','General Service - Recycling & Waste Management',
    'General Service - Sporting and Recreational Camps','General Service - Utility',
    'General Service - Miscellaneous Personal Services',
    'General Service - Membership Organizations (Civic, Social, and Fraternal)',
    'General Service - Membership Organizations (Non-sports)',
    'General Service - Membership Organizations (Professional Sports Clubs)',
    'General Service - Membership Organizations (Recreation Sports Clubs)',
    'General Service - Membership Organizations (Automobile Associations)',
    'General Services - Cleaning, Maintenance, and Janitorial Services',
    'General Services - Exterminating and Disinfecting Services',
    'General Services - Laundries Family and Commercial','General Services - Laundry, Cleaning, and Garment Services',
    'General Services - Testing Laboratories (Non-Medical Testing)','General Services - Dry Cleaners',
    'General Services - Buying and Shopping Services and Clubs','General Services - Carpet and Upholstery Cleaning',
  ].map(v => ({ label: v, value: v }))},
  { label: '🏥 Medical Services', options: [
    'Medical Services - Hospitals','Medical Services - Telemedicine & Telehealth',
    'Medical Services - Doctors and Physicians','Medical Services - Dentists and Orthodontists',
    'Medical Services - Optometrists and Ophthalmologists','Medical Services - Chiropractors',
    'Medical Services - Opticians, Optical Goods, and Eyeglasses','Medical Services - Osteopaths',
    'Medical Services - Podiatrists and Chiropodists','Medical Services - Veterinary Services',
    'Medical Services - Medical and Dental Laboratories','Medical Services - Nursing and Personal Care Facilities',
  ].map(v => ({ label: v, value: v }))},
  { label: '📦 Others', options: [
    'Others - Cryptocurrency','Others - Funding, Seed Capital Investment, and Venture Capital',
    'Others - Books, Periodicals and Newspapers','Others - Boat Dealers',
    'Others - Camper, Recreational and Utility Trailer Dealers','Others - Public Golf Courses',
    'Others - Parking Lots, Parking Meters and Garages','Others - Motor Homes Dealers',
    'Others - Motor Home and Recreational Vehicle Rentals','Others - Motion Picture and Video Tape Production and Distribution',
    'Others - Mobile Home Dealers','Others - Miscellaneous Automotive, Aircraft, and Farm Equipment Dealers',
    'Others - Information Retrieval Services','Others - Electric Vehicle Charging','Others - Snowmobile Dealers',
    'Others - Computer Maintenance, Repair and Services','Others - Trailer Parks and Campgrounds',
    'Others - Automated Fuel Dispensers','Others - Truck and Utility Trailer Rentals',
    'Others - Video Amusement Game Supplies','Others - Public Warehousing and Storage',
    'Others - Swimming Pools Sales and Service','Others - Telegraph Services','Others - Tent and Awning Shops',
    'Others - Fireplace, Fireplace Screens and Accessories Stores','Others - Package Tour Operators',
  ].map(v => ({ label: v, value: v }))},
  { label: '🏪 Other Offline', options: [
    'Other Offline - Eating Places and Restaurants','Other Offline - Fast Food Restaurants','Other Offline - Caterers',
    'Other Offline - Drinking Places (Alcoholic Beverages)','Other Offline - Beauty and Barber Shops',
    'Other Offline - Car Washes','Other Offline - Advertising Services',
    'Other Offline - Automotive Service Shops (Non-Dealer)','Other Offline - Automotive Body Repair Shops',
    'Other Offline - Automotive Paint Shops','Other Offline - Air Conditioning and Refrigeration Repair Shops',
    'Other Offline - Bowling Alleys','Other Offline - Billiard and Pool Establishments',
    'Other Offline - Bands, Orchestras, and Miscellaneous Entertainers',
    'Other Offline - Electrical Contractors','Other Offline - Electronics Repair Shops',
    'Other Offline - Electrical and Small Appliance Repair Shops','Other Offline - Carpentry Contractors',
    'Other Offline - Concrete Work Contractors','Other Offline - Dance Halls, Studios and Schools',
    'Other Offline - Door To Door Sales','Other Offline - DVD/Video Tape Rental Stores',
    'Other Offline - Emergency Services (GCAS)','Other Offline - Funeral Services and Crematories',
    'Other Offline - Furniture Reupholstery, Repair, and Refinishing',
    'Other Offline - Heating, Plumbing, and Air Conditioning Contractors',
    'Other Offline - Masonry, Stonework, Tile Setting, Plastering and Insulation Contractors',
    'Other Offline - Miscellaneous Repair Shops and Related Services','Other Offline - Motion Picture Theaters',
    'Other Offline - News Dealers and Newsstands','Other Offline - Record Stores',
    'Other Offline - Roofing, Siding, and Sheet Metal Work Contractors',
    'Other Offline - Service Stations','Other Offline - Shoe Repair Shops, Shoe Shine Parlors, and Hat Cleaning Shops',
    'Other Offline - Special Trade Contractors','Other Offline - Specialty Cleaning, Polishing and Sanitation Preparations',
    'Other Offline - Tire Retreading and Repair Shops','Other Offline - Tolls and Bridge Fees',
    'Other Offline - Towing Services','Other Offline - Video Game Arcades/Establishments',
    'Other Offline - Watch, Clock and Jewelry Repair','Other Offline - Welding Services',
    'Other Offline - Wholesale Clubs','Other Offline - Wrecking and Salvage Yards',
  ].map(v => ({ label: v, value: v }))},
  { label: '🌐 Platforms', options: [
    'Platforms - Goods Marketplace','Platforms - Goods Marketplace (with dedicated brand stores)',
    'Platforms - Services Marketplace','Platforms - Digital Content Marketplace & Appstore',
    'Platforms - Super Apps','Platforms - Social Media','Platforms - Other Platforms',
  ].map(v => ({ label: v, value: v }))},
  { label: '💼 Professional Service', options: [
    'Professional Service - App/Website/Software Development & Maintenance',
    'Professional Service - BPO & KPO','Professional Service - Consulting & Coaching',
    'Professional Service - Content Writing & Proof Reading','Professional Service - Counseling Services',
    'Professional Service - Credit Rating & Financial Data','Professional Service - Customer Survey & Market Research',
    'Professional Service - Design & Architecture (Landscape and Horticultural)',
    'Professional Service - Design & Architecture (Others)','Professional Service - Freelance (Graphic Design)',
    'Professional Service - Freelance (Others)','Professional Service - Real Estate Sales & Brokerage',
    'Professional Service - Printing, publishing and book binding',
    'Professional Service - Film development, photographic prints',
    'Professional Service - Photocopying, Reproduction, and Blueprinting Services',
    'Professional Service - Typesetting, Plate Making and Related Services',
    'Professional Services - Accounting, Auditing, Payroll, Bookkeeping Services',
    'Professional Services - Architectural, Engineering, and Surveying Services',
    'Professional Services - Legal Advices and Services','Professional Services - Tax Preparation Services',
    'Professional Services - Employment Agencies and Temporary Help Services',
    'Professional Services - Detective Agencies, Protective Services, and Security Services',
    'Professional Services - Airports, Flying Fields, and Airport Terminals',
    'Professional Services - Motor Freight Carriers and Trucking',
    'Professional Services - General Contractors','Professional Services - Courier Services',
    'Professional Services (Govt) - Court Costs','Professional Services (Govt) - Fines',
    'Professional Services (Govt) - Not elsewhere classified','Professional Services (Govt) - Postal',
    'Professional Services (Govt) - Tax Payments',
    'Professional Services (Govt) - U.S. Federal Government Agencies or Departments',
    'Professional Services (Govt) - Bail and Bond Payments',
  ].map(v => ({ label: v, value: v }))},
  { label: '☁️ Software as a Service', options: [
    'Software as a Service - Enterprise Software','Software as a Service - Education Technology (EdTech)',
    'Software as a Service - Employer on Record','Software as a Service - Benefits, Rewards & Loyalty',
    'Software as a Service - Other Software as a Service',
  ].map(v => ({ label: v, value: v }))},
  { label: '📡 Telecommunication', options: [
    'Telecommunication Services - Telecommunication Services',
  ].map(v => ({ label: v, value: v }))},
  { label: '✈️ Travel & Leisure', options: [
    'Travel & Leisure - Accommodation (Hotels, etc)','Travel & Leisure - Airlines',
    'Travel & Leisure - B2B Travel','Travel & Leisure - Boat Rentals and Leasing',
    'Travel & Leisure - Cruise Lines','Travel & Leisure - Destination Management Companies',
    'Travel & Leisure - Event Ticketing','Travel & Leisure - Marinas, Marine Service, and Supplies',
    'Travel & Leisure - Travel Agencies & OTAs',
    'Travel & Leisure - Tourist Attractions (Tourist Attractions and Exhibits)',
    'Travel & Leisure - Tourist Attractions (Aquariums, Seaquariums, Dolphinariums, and Zoos)',
    'Travel & Leisure - Tourist Attractions (Amusement Parks, Circuses, Carnivals)',
    'Travel & Leisure - Other Transportation Services (Taxicabs and Limousines)',
    'Travel & Leisure - Other Transportation Services (Railroads)',
    'Travel & Leisure - Other Transportation Services (Ambulance Services)',
    'Travel & Leisure - Other Transportation Services (Automobile Rental Agency)',
    'Travel & Leisure - Other Transportation Services (Bus Lines)',
    'Travel & Leisure - Other Transportation Services (Local and Suburban Commuter Passenger Transportation)',
    'Travel & Leisure - Other Transportation Services (Passenger Railways)',
    'Travel & Leisure - Other Transportation Services',
    'Travel & Leisure - Other Travel & Leisure (Timeshares)',
    'Travel & Leisure - Other Travel & Booking services (e.g., Visa)',
  ].map(v => ({ label: v, value: v }))},
];
const TAZAPAY_LEGAL_ROLES = ['director', 'shareholder', 'beneficial_owner', 'authorised_signatory', 'authorised_representative', 'other'];
const TAZAPAY_DOC_TYPES_COMPANY = [
  { value: 'registrationProofDoc', label: '📄 注册证明' },
  { value: 'AddressProofDoc', label: '🏠 地址证明' },
  { value: 'beneficialOwnershipDoc', label: '👥 受益所有权' },
  { value: 'bankStatementProofDoc', label: '🏦 银行流水' },
  { value: 'previousCardStatementProofDoc', label: '💳 历史卡账单' },
  { value: 'sampleInvoicesDoc', label: '🧾 样本发票' },
  { value: 'additionalDocs', label: '📎 补充材料' },
];
const TAZAPAY_DOC_TYPES_LEGAL = [
  { value: 'passport', label: '🛂 护照' },
  { value: 'id_front', label: '🪪 身份证正面' },
  { value: 'id_back', label: '🪪 身份证背面' },
  { value: 'AddressProofDoc', label: '🏠 地址证明' },
  { value: 'additionalDocs', label: '📎 补充材料' },
];

const MDAQ_BUSINESS_TYPES = [
  'Information Technology', 'Financial Services', 'E-Commerce', 'Manufacturing',
  'Wholesale Trading', 'Professional Services', 'Logistics and Transportation',
  'Food and Drinks', 'Travel Agency', 'Other',
];
const MDAQ_SOURCE_OF_FUNDS = ['first_party_only', 'related_third_party'];
const MDAQ_OWNERSHIP_LAYERS = ['1', '2', '3_or_more', 'unknown'];
const MDAQ_PAYMENT_MODES = ['telegraphic_transfer', 'card', 'cash', 'cheque', 'other'];
const MDAQ_ONBOARDING_MODES = ['face_to_face', 'non_face_to_face'];
const MDAQ_PRODUCT_COMPLEXITY = ['simple', 'medium', 'complex'];
const MDAQ_ENTITY_TYPES = ['private_company', 'public_company', 'partnership', 'sole_proprietorship', 'trust', 'other'];

const CURRENCY_LIST = [
  'USD','EUR','GBP','SGD','HKD','JPY','AUD','CAD','CHF','CNY',
  'INR','MYR','THB','PHP','IDR','VND','KRW','TWD','NZD','SEK',
  'NOK','DKK','PLN','CZK','HUF','RON','BGN','HRK','TRY','ZAR',
  'BRL','MXN','COP','CLP','PEN','ARS','AED','SAR','QAR','KWD',
  'BHD','OMR','JOD','EGP','NGN','KES','GHS','TZS','UGX',
].map(c => ({ label: c, value: c }));

const sectionStyle = { fontSize: 13, fontWeight: 600, color: '#1e293b', marginBottom: 12, paddingBottom: 6, borderBottom: '1px solid #f0f0f0' };
const sectionStyleMt = { ...sectionStyle, marginTop: 8 };
const gridCol2 = { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 };
const gridCol3 = { display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 16 };
const gridCol4 = { display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: 16 };

/* ── Address sub-form ── */
function AddressFields({ prefix, label, required = true, t }) {
  return (
    <div style={{ background: '#f9fafb', borderRadius: 8, padding: '12px 16px', marginBottom: 12, border: '1px solid #e5e7eb' }}>
      <div style={{ fontSize: 12, fontWeight: 600, color: '#6b7280', marginBottom: 8 }}>📍 {label}{!required && ` (${t('optional')})`}</div>
      <div style={gridCol3}>
        <Form.Item name={[...prefix, 'country']} label={t('tzAddressCountry')} rules={[{ required }]}><Input placeholder="SG" /></Form.Item>
        <Form.Item name={[...prefix, 'state']} label={t('tzAddressState')} rules={[{ required }]}><Input placeholder="Singapore" /></Form.Item>
        <Form.Item name={[...prefix, 'city']} label={t('tzAddressCity')} rules={[{ required }]}><Input placeholder="Singapore" /></Form.Item>
      </div>
      <div style={gridCol2}>
        <Form.Item name={[...prefix, 'postcode']} label={t('tzAddressPostcode')} rules={[{ required }]}><Input placeholder="123456" /></Form.Item>
        <Form.Item name={[...prefix, 'line1']} label={t('tzAddressLine1')} rules={[{ required }]}><Input placeholder="1 Test Street" /></Form.Item>
      </div>
    </div>
  );
}

/* ── Build Tazapay extParam JSON from form values ── */
function buildTazapayExtParam(values) {
  const ext = {};
  if (values.tz_email) ext.email = values.tz_email;
  if (values.tz_description) ext.description = values.tz_description;
  if (values.tz_phone) ext.phone = values.tz_phone;
  if (values.tz_phoneCode) ext.phoneCode = values.tz_phoneCode;
  if (values.tz_tax) ext.tax = values.tz_tax;
  if (values.tz_website) ext.website = values.tz_website;
  ext.industry = values.tz_industry;
  if (values.tz_tp_value || values.tz_tp_volume) {
    ext.transactionProfile = {
      monthlyExpectedTransactionsValue: values.tz_tp_value,
      monthlyExpectedTransactionVolume: values.tz_tp_volume,
      expectedTransactionCountries: values.tz_tp_countries ? values.tz_tp_countries.split(',').map(s => s.trim()) : undefined,
      expectedTransactionCurrencies: values.tz_tp_currencies ? values.tz_tp_currencies.split(',').map(s => s.trim()) : undefined,
      customerRisk: values.tz_tp_risk,
    };
  }
  ext.kybInfo = {
    companyType: values.tz_companyType, identifyNo: values.tz_identifyNo,
    companyName: values.tz_companyName,
    registerAddress: values.tz_registerAddress, operationAddress: values.tz_operationAddress,
    attachments: (values.tz_companyDocs || []).map(d => ({ fileType: d.fileType, name: d.name, describe: d.describe, fileId: d.fileId })),
    legalInfo: (values.tz_legalInfo || []).map(l => {
      const legal = {
        name: l.name, nationality: l.nationality,
        role: l.role, ownershipPercentage: l.ownershipPercentage,
        attachments: (l.attachments || []).map(d => ({ fileType: d.fileType, name: d.name, describe: d.describe, fileId: d.fileId })),
      };
      if (l.lastName) legal.lastName = l.lastName;
      if (l.dateOfBirth) legal.dateOfBirth = typeof l.dateOfBirth === 'string' ? l.dateOfBirth : l.dateOfBirth.format('YYYY-MM-DD');
      if (l.phone) legal.phone = l.phone;
      if (l.phoneCode) legal.phoneCode = l.phoneCode;
      if (l.residentialAddress?.country) legal.residentialAddress = l.residentialAddress;
      return legal;
    }),
  };
  if (values.tz_establishDate) ext.kybInfo.establishDate = typeof values.tz_establishDate === 'string' ? values.tz_establishDate : values.tz_establishDate.format('YYYY-MM-DD');
  if (values.tz_statementDescribe) ext.kybInfo.statementDescribe = values.tz_statementDescribe;
  return JSON.stringify(ext);
}

/* ── Build MDAQ (KYB) extParam JSON from form values ── */
function buildMdaqExtParam(values) {
  const ext = {
    email: values.mdaq_email,
    firstName: values.mdaq_firstName,
    lastName: values.mdaq_lastName,
    countryCode: values.mdaq_countryCode,
  };
  if (values.mdaq_language) ext.language = values.mdaq_language;

  const company = {
    companyName: values.mdaq_companyName,
    businessType: values.mdaq_businessType,
    registrationNumber: values.mdaq_registrationNumber,
    companyAddress: values.mdaq_companyAddress,
    postalCode: values.mdaq_postalCode,
    city: values.mdaq_city,
    state: values.mdaq_state,
    countryOfIncorporation: values.mdaq_countryOfIncorporation,
    countryOfOperations: values.mdaq_countryOfOperations,
  };
  if (values.mdaq_incorporationDate) {
    company.incorporationDate = typeof values.mdaq_incorporationDate === 'string'
      ? values.mdaq_incorporationDate
      : values.mdaq_incorporationDate.format('DD-MM-YYYY');
  }
  if (values.mdaq_sourceOfFunds) company.sourceOfFunds = values.mdaq_sourceOfFunds;
  ext.companyInfo = company;

  // cddInfo 全部可选，任一有值才组装
  const cdd = {};
  const cddMap = {
    customerBusinessWebsite: values.mdaq_cdd_website,
    customerBusinessDescription: values.mdaq_cdd_description,
    customerAcquisitionChannel: values.mdaq_cdd_acquisitionChannel,
    entityType: values.mdaq_cdd_entityType,
    ownershipStructureLayers: values.mdaq_cdd_ownershipLayers,
    paymentModes: values.mdaq_cdd_paymentModes,
    onboardingMode: values.mdaq_cdd_onboardingMode,
    productServiceComplexity: values.mdaq_cdd_productComplexity,
    topCounterPartiesInbound: values.mdaq_cdd_inbound,
    topCounterPartiesOutbound: values.mdaq_cdd_outbound,
    sourceOfFunds: values.mdaq_cdd_sourceOfFunds,
    purposeOfAccountOpening: values.mdaq_cdd_purpose,
  };
  Object.entries(cddMap).forEach(([k, v]) => { if (v !== undefined && v !== null && v !== '') cdd[k] = v; });
  if (values.mdaq_cdd_anticipatedInbound !== undefined && values.mdaq_cdd_anticipatedInbound !== null) {
    cdd.anticipatedValueMonthlyTransactionsInbound = values.mdaq_cdd_anticipatedInbound;
  }
  if (Object.keys(cdd).length > 0) ext.cddInfo = cdd;

  return JSON.stringify(ext);
}

export default function UserCreate() {
  const { t } = useLanguage();
  const [createForm] = Form.useForm();
  const [queryForm] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [queryLoading, setQueryLoading] = useState(false);
  const [createResult, setCreateResult] = useState(null);
  const [queryResult, setQueryResult] = useState(null);
  const [activeTab, setActiveTab] = useState('create');
  const [selectedCreateChannel, setSelectedCreateChannel] = useState(null);
  const isTazapay = selectedCreateChannel === 'TAZAPAY';
  const isMdaq = selectedCreateChannel === 'MDAQ';
  const [, forceUpdate] = useState(0);
  const [uploadedFiles, setUploadedFiles] = useState([]);

  const loadUploadedFiles = async () => {
    const uid = localStorage.getItem('uid') || localStorage.getItem('username');
    if (!uid) return;
    try {
      const { data } = await metaApi.getUploadedFiles(uid, 'TAZAPAY');
      const list = data?.data || [];
      setUploadedFiles(Array.isArray(list) ? list : []);
    } catch (e) {
      console.error('Failed to load uploaded files:', e);
    }
  };

  const getFileIdOptionsByType = (docType) => {
    const files = (docType ? uploadedFiles.filter(f => f.documentType === docType) : uploadedFiles)
      .filter(f => f.s3Exists !== false);
    return files.map(f => {
      const fid = f.fileId ? f.fileId.slice(-6) : '';
      let timeStr = '';
      if (f.createdAt) {
        const d = new Date(typeof f.createdAt === 'number' ? f.createdAt : Date.parse(f.createdAt));
        timeStr = isNaN(d.getTime()) ? '' : d.toLocaleString();
      }
      return {
        label: `📄 ${f.originalFileName || f.fileId} [${fid}]${timeStr ? ` ${timeStr}` : ''}`,
        value: f.localFilePath || f.storageObjectKey || f.fileId,
        originalFileName: f.originalFileName || f.fileId,
      };
    });
  };

  const handleCreateChannelChange = (ch) => {
    setSelectedCreateChannel(ch);
    // 仅在外部用户ID 为空时预填登录名作为默认值，避免覆盖用户已手动输入的值
    const uid = localStorage.getItem('uid') || localStorage.getItem('username') || '';
    if (!createForm.getFieldValue('externalUserId')) {
      createForm.setFieldsValue({ externalUserId: uid });
    }
    if (ch === 'TAZAPAY') {
      createForm.setFieldsValue({ userType: 'BUSINESS' });
      loadUploadedFiles();
    }
    if (ch === 'MDAQ') {
      // MDAQ 本期仅 KYB（BUSINESS），币种默认 SGD
      createForm.setFieldsValue({ userType: 'BUSINESS', currency: 'SGD' });
    }
  };

  useEffect(() => { testApi.getChannelCurrencies?.().catch(() => {}); }, []);

  const handleCreate = async (values) => {
    setLoading(true);
    try {
      let extParam = values.extParam || undefined;
      if (isTazapay) extParam = buildTazapayExtParam(values);
      if (isMdaq) extParam = buildMdaqExtParam(values);
      const requestBody = {
        requestId: `req_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`,
        channelCode: values.channelCode, externalUserId: values.externalUserId,
        userType: values.userType, currency: values.currency || undefined,
        operateType: values.operateType || 'CREATE', extParam,
      };
      const username = localStorage.getItem('username') || 'admin';
      const { data } = await testApi.executeTest({
        testType: 'USER_CREATE', channelCode: values.channelCode,
        businessPartner: values.businessPartner || 'test',
        currency: values.currency || '', env: 'SANDBOX', executor: username,
        requestBody: JSON.stringify(requestBody),
      });
      const res = data;
      if (res.data?.responseBody && typeof res.data.responseBody === 'string') {
        try { res.data.responseBody = JSON.parse(res.data.responseBody); } catch {}
      }
      setCreateResult(res);
      if (res.data?.success) message.success(t('userCreateSubmitted'));
      else message.warning(`${t('userCreateFailed')}: ${res.data?.errorMessage || res.msg || t('unknownError')}`);
    } catch (error) {
      message.error(t('userCreateFailed') + ': ' + (error.response?.data?.msg || error.message));
      setCreateResult(error.response?.data || { error: error.message });
    } finally { setLoading(false); }
  };

  const handleQuery = async (values) => {
    setQueryLoading(true);
    try {
      const requestBody = { requestId: `req_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`, externalUserId: values.externalUserId || undefined, userId: values.userId || undefined };
      const { data } = await testApi.executeTest({ testType: 'USER_QUERY', channelCode: '', businessPartner: values.businessPartner || 'test', currency: '', env: 'SANDBOX', executor: localStorage.getItem('username') || 'admin', requestBody: JSON.stringify(requestBody) });
      const res = data;
      if (res.data?.responseBody && typeof res.data.responseBody === 'string') { try { res.data.responseBody = JSON.parse(res.data.responseBody); } catch {} }
      setQueryResult(res);
    } catch (error) { message.error(t('userQueryFailed') + ': ' + (error.response?.data?.msg || error.message)); setQueryResult(error.response?.data || { error: error.message }); }
    finally { setQueryLoading(false); }
  };

  const handleReset = () => { createForm.resetFields(); queryForm.resetFields(); setCreateResult(null); setQueryResult(null); setSelectedCreateChannel(null); };

  /* ── Tazapay KYB form fields ── */
  const renderTazapayFields = () => (
    <>
      <div style={sectionStyleMt}>🏢 {t('tzCompanyBasicInfo')}</div>
      <div style={gridCol2}>
        <Form.Item name="tz_industry" label={t('tzIndustry')} rules={[{ required: true }]} tooltip={t('tzIndustryRequired')}><Select showSearch optionFilterProp="label" placeholder="选择行业" options={TAZAPAY_INDUSTRY_OPTIONS} /></Form.Item>
        <Form.Item name="tz_email" label={t('email')}><Input placeholder="company@example.com" /></Form.Item>
      </div>
      <div style={gridCol3}>
        <Form.Item name="tz_phone" label={t('tzPhoneLabel')}><Input placeholder="1234567890" /></Form.Item>
        <Form.Item name="tz_phoneCode" label={t('tzPhoneCode')}><Input placeholder="65" /></Form.Item>
        <Form.Item name="tz_tax" label={t('tzTaxId')}><Input placeholder="T12345678X" /></Form.Item>
      </div>
      <div style={gridCol2}>
        <Form.Item name="tz_website" label={t('tzWebsite')}><Input placeholder="https://example.com" /></Form.Item>
        <Form.Item name="tz_description" label={t('tzBusinessDesc')}><Input placeholder="Test company description" /></Form.Item>
      </div>

      <div style={sectionStyleMt}>📋 {t('tzKybCompanyInfo')}</div>
      <div style={gridCol3}>
        <Form.Item name="tz_companyType" label={t('tzCompanyTypeLabel')} rules={[{ required: true }]}><Select options={TAZAPAY_COMPANY_TYPES.map(v => ({ label: v, value: v }))} /></Form.Item>
        <Form.Item name="tz_companyName" label={t('tzCompanyNameLabel')} rules={[{ required: true }]}><Input placeholder="Test Company Ltd" /></Form.Item>
        <Form.Item name="tz_identifyNo" label={t('tzRegNo')} rules={[{ required: true }]}><Input placeholder="REG123456" /></Form.Item>
      </div>
      <div style={gridCol2}>
        <Form.Item name="tz_establishDate" label={t('tzEstablishDate')}><DatePicker style={{ width: '100%' }} format="YYYY-MM-DD" placeholder={t('tzSelectDate')} /></Form.Item>
        <Form.Item name="tz_statementDescribe" label={t('tzStatementDesc')}><Input placeholder={t('optional')} /></Form.Item>
      </div>

      <AddressFields prefix={['tz_registerAddress']} label={t('tzRegisterAddress')} t={t} />
      <AddressFields prefix={['tz_operationAddress']} label={t('tzOperationAddress')} t={t} />

      <div style={sectionStyleMt}>📎 {t('tzCompanyDocs')}</div>
      <Form.List name="tz_companyDocs" initialValue={[{ fileType: 'registrationProofDoc', name: '', describe: '', fileId: '' }]}>
        {(fields, { add, remove }) => (<>
          {fields.map(({ key, name, ...rest }) => (
            <div key={key} style={{ background: '#f0f5ff', borderRadius: 8, padding: '12px 16px', marginBottom: 10, border: '1px solid #d6e4ff', position: 'relative' }}>
              {fields.length > 1 && <MinusCircleOutlined onClick={() => remove(name)} style={{ color: '#ff4d4f', position: 'absolute', top: 12, right: 12, cursor: 'pointer' }} />}
              <div style={gridCol2}>
                <Form.Item {...rest} name={[name, 'fileType']} label={t('tzFileType')} rules={[{ required: true }]} style={{ marginBottom: 8 }}>
                  <Select placeholder={t('tzFileType')} options={TAZAPAY_DOC_TYPES_COMPANY} onChange={() => { createForm.setFieldValue(['tz_companyDocs', name, 'fileId'], undefined); forceUpdate(c => c + 1); }} />
                </Form.Item>
                <Form.Item {...rest} name={[name, 'fileId']} label={t('tzLinkedFile')} rules={[{ required: true }]} style={{ marginBottom: 8 }}>
                  <Select placeholder={t('tzSelectUploadedFile')} showSearch optionFilterProp="label" options={getFileIdOptionsByType(createForm.getFieldValue(['tz_companyDocs', name, 'fileType']))}
                    onChange={(val) => {
                      const file = uploadedFiles.find(f => (f.localFilePath || f.storageObjectKey || f.fileId) === val);
                      if (file) createForm.setFieldValue(['tz_companyDocs', name, 'name'], file.originalFileName || file.fileId);
                    }} />
                </Form.Item>
              </div>
              <div style={gridCol2}>
                <Form.Item {...rest} name={[name, 'name']} label={t('tzFileName')} rules={[{ required: true }]} style={{ marginBottom: 0 }}><Input placeholder="registration.pdf" /></Form.Item>
                <Form.Item {...rest} name={[name, 'describe']} label={t('tzFileDesc')} rules={[{ required: true }]} style={{ marginBottom: 0 }}><Input placeholder="Company registration" /></Form.Item>
              </div>
            </div>
          ))}
          <Button type="dashed" onClick={() => add({ fileType: 'additionalDocs' })} icon={<PlusOutlined />} block style={{ marginBottom: 12 }}>{t('tzAddDoc')}</Button>
        </>)}
      </Form.List>

      <div style={sectionStyleMt}>👤 {t('tzLegalInfo')}</div>
      <Form.List name="tz_legalInfo" initialValue={[{ name: '', lastName: '', nationality: '', role: ['director'], ownershipPercentage: 100, dateOfBirth: '', phone: '', phoneCode: '', attachments: [{ fileType: 'passport', name: '', describe: '', fileId: '' }] }]}>
        {(fields, { add, remove }) => (<>
          {fields.map(({ key, name, ...rest }) => (
            <div key={key} style={{ background: '#fefce8', borderRadius: 8, padding: '12px 16px', marginBottom: 12, border: '1px solid #fde68a' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
                <Tag color="orange">{t('tzRepresentative')} #{name + 1}</Tag>
                {fields.length > 1 && <MinusCircleOutlined onClick={() => remove(name)} style={{ color: '#ff4d4f', cursor: 'pointer' }} />}
              </div>
              <div style={gridCol4}>
                <Form.Item {...rest} name={[name, 'name']} label={t('tzFirstName')} rules={[{ required: true }]}><Input /></Form.Item>
                <Form.Item {...rest} name={[name, 'lastName']} label={t('tzLastName')}><Input /></Form.Item>
                <Form.Item {...rest} name={[name, 'nationality']} label={t('tzNationality')} rules={[{ required: true }]}><Input placeholder="SG" /></Form.Item>
                <Form.Item {...rest} name={[name, 'dateOfBirth']} label={t('tzDob')} rules={[{ required: true, message: t('tzSelectDob') }]}><DatePicker style={{ width: '100%' }} format="YYYY-MM-DD" placeholder={t('tzSelectDate')} /></Form.Item>
              </div>
              <div style={gridCol4}>
                <Form.Item {...rest} name={[name, 'role']} label={t('tzRole')} rules={[{ required: true }]}><Select mode="multiple" options={TAZAPAY_LEGAL_ROLES.map(v => ({ label: v, value: v }))} /></Form.Item>
                <Form.Item {...rest} name={[name, 'ownershipPercentage']} label={t('tzOwnership')} rules={[({ getFieldValue }) => ({
                  validator(_, value) {
                    const roles = getFieldValue(['tz_legalInfo', name, 'role']) || [];
                    const need = roles.includes('shareholder') || roles.includes('beneficial_owner');
                    if (need && (value === undefined || value === null || value === '')) {
                      return Promise.reject(new Error('持股相关角色(shareholder/beneficial_owner)必填持股比例'));
                    }
                    return Promise.resolve();
                  }
                })]}><InputNumber min={0} max={100} style={{ width: '100%' }} /></Form.Item>
                <Form.Item {...rest} name={[name, 'phone']} label={t('tzPhoneLabel')}><Input /></Form.Item>
                <Form.Item {...rest} name={[name, 'phoneCode']} label={t('tzPhoneCode')}><Input placeholder="65" /></Form.Item>
              </div>
              <Form.Item {...rest} name={[name, 'residentialAddress']} noStyle><Input type="hidden" /></Form.Item>
              <AddressFields prefix={['tz_legalInfo', name, 'residentialAddress']} label={t('tzResidentialAddress')} required={false} t={t} />
              <div style={{ fontSize: 12, fontWeight: 600, color: '#6b7280', marginBottom: 6 }}>📎 {t('tzRepDocs')}</div>
              <Form.List name={[name, 'attachments']}>
                {(docFields, docOps) => (<>
                  {docFields.map(({ key: dk, name: dn, ...dr }) => (
                    <div key={dk} style={{ background: '#fff', borderRadius: 6, padding: '10px 12px', marginBottom: 8, border: '1px solid #e5e7eb', position: 'relative' }}>
                      {docFields.length > 1 && <MinusCircleOutlined onClick={() => docOps.remove(dn)} style={{ color: '#ff4d4f', position: 'absolute', top: 10, right: 10, cursor: 'pointer' }} />}
                      <div style={gridCol2}>
                        <Form.Item {...dr} name={[dn, 'fileType']} label={t('tzFileType')} rules={[{ required: true }]} style={{ marginBottom: 8 }}>
                          <Select placeholder={t('tzFileType')} options={TAZAPAY_DOC_TYPES_LEGAL} onChange={() => { createForm.setFieldValue(['tz_legalInfo', name, 'attachments', dn, 'fileId'], undefined); forceUpdate(c => c + 1); }} />
                        </Form.Item>
                        <Form.Item {...dr} name={[dn, 'fileId']} label={t('tzLinkedFile')} rules={[{ required: true }]} style={{ marginBottom: 8 }}>
                          <Select placeholder={t('tzSelectUploadedFile')} showSearch optionFilterProp="label" options={getFileIdOptionsByType(createForm.getFieldValue(['tz_legalInfo', name, 'attachments', dn, 'fileType']))}
                            onChange={(val) => {
                              const file = uploadedFiles.find(f => (f.localFilePath || f.storageObjectKey || f.fileId) === val);
                              if (file) createForm.setFieldValue(['tz_legalInfo', name, 'attachments', dn, 'name'], file.originalFileName || file.fileId);
                            }} />
                        </Form.Item>
                      </div>
                      <div style={gridCol2}>
                        <Form.Item {...dr} name={[dn, 'name']} label={t('tzFileName')} rules={[{ required: true }]} style={{ marginBottom: 0 }}><Input placeholder="passport.pdf" /></Form.Item>
                        <Form.Item {...dr} name={[dn, 'describe']} label={t('tzFileDesc')} rules={[{ required: true }]} style={{ marginBottom: 0 }}><Input placeholder="Passport" /></Form.Item>
                      </div>
                    </div>
                  ))}
                  <Button type="dashed" size="small" onClick={() => docOps.add({ fileType: 'additionalDocs' })} icon={<PlusOutlined />} block>{t('tzAddRepDoc')}</Button>
                </>)}
              </Form.List>
            </div>
          ))}
          <Button type="dashed" onClick={() => add({ role: ['director'], ownershipPercentage: 0, attachments: [{ fileType: 'passport' }] })} icon={<PlusOutlined />} block style={{ marginBottom: 12 }}>{t('tzAddRepresentative')}</Button>
        </>)}
      </Form.List>

      <div style={sectionStyleMt}>📊 {t('tzTransactionProfile')}</div>
      <div style={gridCol4}>
        <Form.Item name="tz_tp_value" label={t('tzMonthlyValue')}><InputNumber style={{ width: '100%' }} placeholder="10000" /></Form.Item>
        <Form.Item name="tz_tp_volume" label={t('tzMonthlyVolume')}><InputNumber style={{ width: '100%' }} placeholder="100" /></Form.Item>
        <Form.Item name="tz_tp_countries" label={t('tzCountries')}><Input placeholder="SG,US" /></Form.Item>
        <Form.Item name="tz_tp_risk" label={t('tzRiskLevel')}><Select allowClear options={[{ label: 'low', value: 'low' }, { label: 'medium', value: 'medium' }, { label: 'high', value: 'high' }]} /></Form.Item>
      </div>
    </>
  );

  /* ── MDAQ KYB form fields ── */
  const renderMdaqFields = () => (
    <>
      <div style={sectionStyleMt}>🏢 申请人基础信息</div>
      <div style={gridCol2}>
        <Form.Item name="mdaq_email" label="邮箱 (email)" rules={[{ required: true }]}><Input placeholder="company@example.com" /></Form.Item>
        <Form.Item name="mdaq_countryCode" label="国家码 (countryCode)" rules={[{ required: true }]} tooltip="alpha-2, 2字符"><Input placeholder="SG" maxLength={2} /></Form.Item>
      </div>
      <div style={gridCol3}>
        <Form.Item name="mdaq_firstName" label="名 (firstName)" rules={[{ required: true }]}><Input placeholder="Skyline Tech" /></Form.Item>
        <Form.Item name="mdaq_lastName" label="姓 (lastName)" rules={[{ required: true }]}><Input placeholder="Pte Ltd" /></Form.Item>
        <Form.Item name="mdaq_language" label="语言 (language)" tooltip="默认 en"><Input placeholder="en" /></Form.Item>
      </div>

      <div style={sectionStyleMt}>📋 公司信息 (companyInfo)</div>
      <div style={gridCol3}>
        <Form.Item name="mdaq_companyName" label="公司名称 (companyName)" rules={[{ required: true }]}><Input placeholder="Skyline Tech Pte. Ltd." /></Form.Item>
        <Form.Item name="mdaq_businessType" label="业务类型 (businessType)" rules={[{ required: true }]}>
          <Select showSearch placeholder="选择业务类型" options={MDAQ_BUSINESS_TYPES.map(v => ({ label: v, value: v }))} />
        </Form.Item>
        <Form.Item name="mdaq_registrationNumber" label="注册号 (registrationNumber)" rules={[{ required: true }]}><Input placeholder="202312345A" /></Form.Item>
      </div>
      <div style={gridCol3}>
        <Form.Item name="mdaq_incorporationDate" label="注册日期 (incorporationDate)" rules={[{ required: true }]} tooltip="格式 dd-MM-yyyy">
          <DatePicker style={{ width: '100%' }} format="DD-MM-YYYY" placeholder="13-01-2000" />
        </Form.Item>
        <Form.Item name="mdaq_countryOfIncorporation" label="注册国 (countryOfIncorporation)" rules={[{ required: true }]} tooltip="alpha-2"><Input placeholder="SG" maxLength={2} /></Form.Item>
        <Form.Item name="mdaq_countryOfOperations" label="经营国 (countryOfOperations)" rules={[{ required: true }]} tooltip="alpha-2"><Input placeholder="SG" maxLength={2} /></Form.Item>
      </div>
      <div style={gridCol2}>
        <Form.Item name="mdaq_companyAddress" label="公司地址 (companyAddress)" rules={[{ required: true }]}><Input placeholder="1 Raffles Place" /></Form.Item>
        <Form.Item name="mdaq_postalCode" label="邮编 (postalCode)" rules={[{ required: true }]}><Input placeholder="048616" /></Form.Item>
      </div>
      <div style={gridCol3}>
        <Form.Item name="mdaq_city" label="城市 (city)" rules={[{ required: true }]}><Input placeholder="Singapore" /></Form.Item>
        <Form.Item name="mdaq_state" label="州/省 (state)" rules={[{ required: true }]}><Input placeholder="Singapore" /></Form.Item>
        <Form.Item name="mdaq_sourceOfFunds" label="资金来源 (sourceOfFunds)">
          <Select allowClear options={MDAQ_SOURCE_OF_FUNDS.map(v => ({ label: v, value: v }))} />
        </Form.Item>
      </div>

      <div style={sectionStyleMt}>🔍 CDD 信息 (cddInfo，全部可选)</div>
      <div style={gridCol2}>
        <Form.Item name="mdaq_cdd_website" label="业务网站 (customerBusinessWebsite)"><Input placeholder="https://skylinetech.com" /></Form.Item>
        <Form.Item name="mdaq_cdd_description" label="业务描述 (customerBusinessDescription)"><Input placeholder="Software development" /></Form.Item>
      </div>
      <div style={gridCol2}>
        <Form.Item name="mdaq_cdd_acquisitionChannel" label="获客渠道 (customerAcquisitionChannel)"><Input placeholder="Merchant of YourCompany" /></Form.Item>
        <Form.Item name="mdaq_cdd_entityType" label="实体类型 (entityType)">
          <Select allowClear options={MDAQ_ENTITY_TYPES.map(v => ({ label: v, value: v }))} />
        </Form.Item>
      </div>
      <div style={gridCol3}>
        <Form.Item name="mdaq_cdd_ownershipLayers" label="股权层级 (ownershipStructureLayers)">
          <Select allowClear options={MDAQ_OWNERSHIP_LAYERS.map(v => ({ label: v, value: v }))} />
        </Form.Item>
        <Form.Item name="mdaq_cdd_paymentModes" label="支付方式 (paymentModes)">
          <Select allowClear options={MDAQ_PAYMENT_MODES.map(v => ({ label: v, value: v }))} />
        </Form.Item>
        <Form.Item name="mdaq_cdd_onboardingMode" label="开户方式 (onboardingMode)">
          <Select allowClear options={MDAQ_ONBOARDING_MODES.map(v => ({ label: v, value: v }))} />
        </Form.Item>
      </div>
      <div style={gridCol3}>
        <Form.Item name="mdaq_cdd_productComplexity" label="产品复杂度 (productServiceComplexity)">
          <Select allowClear options={MDAQ_PRODUCT_COMPLEXITY.map(v => ({ label: v, value: v }))} />
        </Form.Item>
        <Form.Item name="mdaq_cdd_inbound" label="入款对手方 (topCounterPartiesInbound)" tooltip="逗号分隔"><Input placeholder="SG,US,HK" /></Form.Item>
        <Form.Item name="mdaq_cdd_outbound" label="出款对手方 (topCounterPartiesOutbound)" tooltip="逗号分隔"><Input placeholder="SG,US,HK" /></Form.Item>
      </div>
      <div style={gridCol3}>
        <Form.Item name="mdaq_cdd_anticipatedInbound" label="月预计入款 (anticipatedValueMonthlyTransactionsInbound)"><InputNumber style={{ width: '100%' }} placeholder="100000" /></Form.Item>
        <Form.Item name="mdaq_cdd_sourceOfFunds" label="资金来源 (sourceOfFunds)">
          <Select allowClear options={MDAQ_SOURCE_OF_FUNDS.map(v => ({ label: v, value: v }))} />
        </Form.Item>
        <Form.Item name="mdaq_cdd_purpose" label="开户目的 (purposeOfAccountOpening)"><Input placeholder="Business collections" /></Form.Item>
      </div>
    </>
  );

  /* ── Result panel ── */
  const renderResultPanel = (result, emptyIcon, emptyText) => (
    <div style={{ background: '#fff', borderRadius: 8, border: '1px solid #f0f0f0', padding: 20, height: '100%', minHeight: 300 }}>
      {result ? (
        <ReactJson src={result} collapsed={1} theme="rjv-default" displayDataTypes={false} displayObjectSize={false} enableClipboard />
      ) : (
        <div style={{ padding: 60, textAlign: 'center', color: '#9ca3af', background: '#f9fafb', borderRadius: 8, border: '2px dashed #e5e7eb' }}>
          {emptyIcon}
          <div style={{ marginTop: 12 }}>{emptyText}</div>
        </div>
      )}
    </div>
  );

  return (
    <div style={{ padding: '24px 32px', maxWidth: 1600, margin: '0 auto' }}>
      <div className="fade-in-up">
        {/* Page header — matching ChannelAccountManagement style */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
          <Space size={12}>
            <UserAddOutlined style={{ color: '#667eea', fontSize: 20 }} />
            <span style={{ fontSize: 20, fontWeight: 600 }}>{t('channelUserManagement')}</span>
          </Space>
          <Button icon={<ReloadOutlined />} onClick={handleReset}>{t('reset')}</Button>
        </div>

        <Tabs activeKey={activeTab} onChange={setActiveTab} items={[
          /* ── Create Tab ── */
          { key: 'create', label: <span><UserAddOutlined /> {t('createUserTab')}</span>, children: (
            <div style={{ display: 'grid', gridTemplateColumns: (isTazapay || isMdaq) ? '2fr 1fr' : '1fr 1fr', gap: 16 }}>
              <div style={{ background: '#fff', borderRadius: 8, border: '1px solid #f0f0f0', padding: 24 }}>
                <Form form={createForm} layout="vertical" onFinish={handleCreate} initialValues={{ userType: 'INDIVIDUAL', operateType: 'CREATE', businessPartner: 'test', externalUserId: localStorage.getItem('uid') || localStorage.getItem('username') || '' }}>
                  <Form.Item name="businessPartner" hidden><Input /></Form.Item>

                  <div style={sectionStyle}>📋 {t('basicInfo')}</div>
                  <div style={gridCol2}>
                    <Form.Item label={t('channelLabel')} name="channelCode" rules={[{ required: true, message: t('pleaseSelect', t('channelLabel')) }]}>
                      <Select placeholder={t('selectChannelPlaceholder')} onChange={handleCreateChannelChange}
                        options={CHANNELS.map(c => { const logo = getChannelLogo(c); return { label: <Space><span style={{ fontSize: 16 }}>{logo.emoji}</span><span>{logo.name}</span></Space>, value: c }; })} />
                    </Form.Item>
                    <Form.Item label={t('userTypeLabel')} name="userType" rules={[{ required: true }]}>
                      <Select options={[{ label: t('individualUser'), value: 'INDIVIDUAL' }, { label: t('businessUser'), value: 'BUSINESS' }]} />
                    </Form.Item>
                  </div>
                  <div style={gridCol2}>
                    <Form.Item label={t('externalUserIdLabel')} name="externalUserId" tooltip="需与文件上传时填写的外部用户ID保持一致，否则创建用户时捞不到已上传的文件" rules={[{ required: true, message: t('pleaseInputExternalUserId') }]}>
                      <Input style={{ fontFamily: 'monospace' }} placeholder="例如: mdaq_kyb_1780904457" />
                    </Form.Item>
                    <Form.Item label="操作类型 (operateType)" name="operateType" rules={[{ required: true }]}>
                      <Select options={[{ label: 'CREATE (创建)', value: 'CREATE' }, { label: 'UPDATE (更新)', value: 'UPDATE' }]} />
                    </Form.Item>
                  </div>
                  <div style={gridCol2}>
                    <Form.Item label={t('currencyLabel')} name="currency">
                      <Select placeholder={t('selectCurrencyPlaceholder')} showSearch allowClear options={CURRENCY_LIST} />
                    </Form.Item>
                  </div>

                  {isTazapay ? renderTazapayFields() : isMdaq ? renderMdaqFields() : (
                    <>
                      <div style={sectionStyleMt}>🔧 {t('extParamSection')}</div>
                      <Form.Item name="extParam" tooltip={t('extParamTooltip')}>
                        <TextArea rows={3} placeholder='{"key": "value"}' style={{ fontFamily: 'monospace', fontSize: 12 }} />
                      </Form.Item>
                    </>
                  )}

                  <Button type="primary" htmlType="submit" icon={<UserAddOutlined />} loading={loading} size="large" block style={{ height: 44, marginTop: 12 }}>
                    {loading ? t('creatingUser') : t('createUserBtn')}
                  </Button>
                </Form>
              </div>

              <div>
                <div style={sectionStyle}>📋 {t('createResultTitle')}</div>
                {renderResultPanel(createResult,
                  <UserAddOutlined style={{ fontSize: 40, color: '#d1d5db' }} />,
                  t('submitToShowCreateResult')
                )}
              </div>
            </div>
          )},

          /* ── Query Tab ── */
          { key: 'query', label: <span><SearchOutlined /> {t('queryUserTab')}</span>, children: (
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
              <div style={{ background: '#fff', borderRadius: 8, border: '1px solid #f0f0f0', padding: 24 }}>
                <Form form={queryForm} layout="vertical" onFinish={handleQuery} initialValues={{ businessPartner: 'test' }}>
                  <Form.Item name="businessPartner" hidden><Input /></Form.Item>
                  <div style={sectionStyle}>🔍 {t('queryConditionsSection')}</div>
                  <Form.Item label={t('externalUserIdLabel')} name="externalUserId" tooltip={t('externalUserIdTooltip')}><Input placeholder="e.g. user_9988" /></Form.Item>
                  <Form.Item label={t('userIdLabel')} name="userId" tooltip={t('userIdTooltip')}><Input placeholder="e.g. UR20260418..." style={{ fontFamily: 'monospace' }} /></Form.Item>
                  <Button type="primary" htmlType="submit" icon={<SearchOutlined />} loading={queryLoading} size="large" block style={{ height: 44 }}>
                    {queryLoading ? t('queryingUser') : t('queryUserBtn')}
                  </Button>
                </Form>
              </div>

              <div>
                <div style={sectionStyle}>📋 {t('queryResultTitle')}</div>
                {renderResultPanel(queryResult,
                  <SearchOutlined style={{ fontSize: 40, color: '#d1d5db' }} />,
                  t('submitToShowQueryResult')
                )}
              </div>
            </div>
          )},
        ]} />
      </div>
    </div>
  );
}
